import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { LoadingStatus } from '../../../../core/domain/events/state_provider';
import UniqueEntityID from '../../../../core/domain/unique_entity_id';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { DietitianStateProvider } from '../../../../data/dietitian/domain/dietitian_state_provider';
import { ConversationProps } from '../../../../data/message/domain/conversation/conversation';
import { ConversationCommands } from '../../../../data/message/domain/conversation/conversation_commands';
import { MessageProps } from '../../../../data/message/domain/message/message';
import { MessageCommands } from '../../../../data/message/domain/message/message_commands';
import { Patient } from '../../../../data/patient/domain/patient';
import { PatientCommands } from '../../../../data/patient/domain/patient_commands';

export interface MessageData {
  senderId: UniqueEntityID;
  conversationProps: ConversationProps;
  recipientId: UniqueEntityID;
  conversationId: UniqueEntityID;
  userId: UniqueEntityID;
}

export interface MessageViewModel {
  isLoading: boolean;
  error: string;
  dietitian: Dietitian;
}

@Injectable()
export class ConversationFacade {
  constructor(
    private conversationCommands: ConversationCommands,
    private patientCommands: PatientCommands,
    private messageCommands: MessageCommands,
    private dietitianStateProvider: DietitianStateProvider,
  ) {
    // do nothing
  }

  get viewModel$(): Observable<MessageViewModel> {
    return this.dietitianStateProvider.state$.pipe(
      map((e) => {
        return <MessageViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          dietitian: e.entity,
        };
      }),
    );
  }

  loadPatient(patientId: string) {
    return this.patientCommands.getPatient(patientId);
  }

  saveConversation(
    props: ConversationProps,
    messageId: string,
    notify?: boolean,
    markAsRead?: boolean,
  ) {
    return this.conversationCommands.save(props, messageId, notify, markAsRead);
  }

  loadConversation(messageId: string) {
    return this.conversationCommands.findConversation(messageId);
  }

  async sendAudio(data: MessageData, audioBlob: Blob) {
    const uploadResult = await this.messageCommands.uploadAudio(
      data.conversationId.toString(),
      data.userId.toString(),
      audioBlob,
    );

    const props = {
      createdAt: new Date(),
      mimeType: 'audio/*',
      resourcePath: uploadResult.path,
      url: uploadResult.url,
      recipientId: data.recipientId,
      senderId: data.senderId,
    } as MessageProps;

    return this.send(data, props);
  }

  async sendPicture(data: MessageData, imageUrl: string) {
    const uploadResult = await this.messageCommands.uploadPicture(
      data.conversationId.toString(),
      data.userId.toString(),
      imageUrl,
    );

    const props = {
      createdAt: new Date(),
      mimeType: 'image/*',
      resourcePath: uploadResult.path,
      url: uploadResult.url,
      recipientId: data.recipientId,
      senderId: data.senderId,
    } as MessageProps;

    return this.send(data, props);
  }

  async sendMessage(data: MessageData, messageContent: string) {
    const props = {
      createdAt: new Date(),
      message: messageContent,
      recipientId: data.recipientId,
      senderId: data.senderId,
    } as MessageProps;

    return this.send(data, props);
  }

  private async send(data: MessageData, props: MessageProps) {
    const message = await this.messageCommands.save(
      data.conversationId.toString(),
      props,
    );

    this.saveConversation(
      data.conversationProps,
      data.conversationId.toString(),
    );

    return message;
  }

  listenMessages(messageId: string, dietitian: Dietitian, patient: Patient) {
    return this.messageCommands.listen(messageId, dietitian, patient);
  }
}
